<template>
    <!-- A whole card is the link, not a word inside one: a row in a list is
         something to open, and a target the size of a fingertip is the point of
         drawing it as a card at all. -->
    <component
        :is="href ? 'a' : 'div'"
        class="wig-card"
        :class="{ ['wig-card--off']: !href }"
        :href="href"
        :target="href ? '_blank' : null"
        :rel="href ? 'noopener noreferrer' : null"
    >
        <div class="wig-card__head">
            <span class="wig-card__id">
                <span class="wig-card__label" dir="auto">{{ wig.label }}</span>
                <!-- The red wrench the wigs table already marks repairs with —
                     one glyph, and the same one everywhere. -->
                <v-icon
                    v-if="wig.is_repair"
                    small
                    color="#da4453"
                    :title="$t('pages.assistant.card.repair')"
                >
                    mdi-wrench
                </v-icon>
            </span>
            <span v-if="clientName" class="wig-card__client" dir="auto">
                {{ clientName }}
            </span>
        </div>

        <!-- A phone is read left to right in both interface languages. -->
        <p v-if="phone" class="wig-card__phone" dir="ltr">{{ phone }}</p>

        <ul v-if="tags.length" class="wig-card__tags">
            <li
                v-for="tag in tags"
                :key="tag.key"
                class="wig-card__tag"
                :class="tag.class"
                :style="tag.style"
                dir="auto"
            >
                {{ tag.value }}
            </li>
        </ul>
    </component>
</template>

<script>
import getWigColorByState from '@/helpers/getWigColorByState'
import { entityHref } from '../entities'

const DAY = 24 * 60 * 60 * 1000

// Where one unit stops being readable and the next one starts. Under a month
// and a half the day count still means something; past a year nobody counts
// months.
const A_MONTH = 45
const A_YEAR = 365

export default {
    name: 'AssistantWigCard',
    props: {
        wig: {
            type: Object,
            required: true,
        },
        // The client entity the wig points at, already resolved — the wig
        // itself carries only a name and a ref.
        client: {
            type: Object,
            default: null,
        },
    },
    computed: {
        href: function () {
            return entityHref(this.wig)
        },
        /**
         * How long the card has stood still, in whichever unit still carries
         * meaning: 25 d., 2 mo., over 2 y. Nobody reads 743 as anything but
         * "ages", and a third of these cards are past a year.
         *
         * Never worded as a deadline: `stalled_since` is the day the card was
         * last touched and the database holds no due date at all, so a label
         * that sounds like one would be inventing it.
         */
        stalled: function () {
            const days = this.stalledDays

            if (!days) return ''

            return this.$t('pages.assistant.card.stalled', {
                age: this.age(days),
            })
        },
        // `days_stalled` counted for us; `stalled_since` is the same fact as a
        // date, and either may be the one that arrives.
        stalledDays: function () {
            const { days_stalled: days, stalled_since: since } = this.wig

            if (days) return days
            if (!since) return 0

            return Math.max(0, Math.floor((Date.now() - new Date(since)) / DAY))
        },
        // The app already has one colour per state; the card borrows it rather
        // than inventing a second scheme for the same words.
        stateStyle: function () {
            return { background: getWigColorByState(this.wig.state || '') }
        },
        clientName: function () {
            return this.client?.full_name || this.wig.client?.full_name || ''
        },
        phone: function () {
            return this.client?.phone || ''
        },
        /**
         * What the row is: where the card stands, and how long it has stood
         * there. `actual_status` — the production step inside the state, like
         * Skin Matching — is left out on purpose: a list is read by scanning,
         * and the step only matters once the card is open.
         */
        tags: function () {
            const { state } = this.wig

            return [
                state && {
                    key: 'state',
                    value: state,
                    class: 'wig-card__tag--state',
                    style: this.stateStyle,
                },
                this.stalled && {
                    key: 'stalled',
                    value: this.stalled,
                    class: 'wig-card__tag--warn',
                },
                this.wig.barcode && {
                    key: 'barcode',
                    value: this.wig.barcode,
                },
            ].filter(Boolean)
        },
    },
    methods: {
        age: function (days) {
            if (days < A_MONTH) {
                return this.$t('pages.assistant.card.ageDays', { days })
            }

            const months = Math.round(days / 30)

            // Rounding can reach twelve months a few weeks short of the year,
            // and "12 mo." beside "over 1 y." on the next card reads as two
            // different things.
            if (months < 12) {
                return this.$t('pages.assistant.card.ageMonths', { months })
            }

            return this.$t('pages.assistant.card.ageYears', {
                years: Math.max(1, Math.floor(days / A_YEAR)),
            })
        },
    },
}
</script>

<style scoped lang="scss">
// Opaque, not a tint of the bubble behind it: the purple is heavy enough on its
// own, and a translucent card turns the whole list purple on purple. White also
// gives the state colours a surface they were designed for.
.wig-card {
    display: block;
    padding: 10px 12px;
    border-radius: 10px;
    background: $color-primary-light;
    // The accent runs down the leading edge, which swaps with the language.
    border-left: 3px solid #967adc;
    color: $color-secondary-dark;
    text-decoration: none;
    // The bubble around it is `pre-wrap`, because a reply keeps the line breaks
    // it was written with. A card is structure, not preformatted text, and
    // inherits padding out of every template newline if left alone.
    white-space: normal;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.18);
    transition: box-shadow 150ms ease, transform 150ms ease;

    @include for-hebrew {
        border-left: none;
        border-right: 3px solid #967adc;
    }

    & + & {
        margin-top: 8px;
    }

    &__head {
        display: flex;
        align-items: baseline;
        justify-content: space-between;
        gap: 10px;
    }

    // The number and its repair mark are one thing; without the group,
    // `space-between` would push the wrench into the middle of the row.
    &__id {
        display: flex;
        align-items: center;
        gap: 6px;
    }

    &__label {
        font-weight: 700;
        font-size: 14px;
    }

    &__client {
        font-size: 13px;
        color: #55595c;
        text-align: right;

        @include for-hebrew {
            text-align: left;
        }
    }

    &__phone {
        margin: 2px 0 0;
        font-size: 12px;
        color: #8a8f94;
    }

    &__tags {
        display: flex;
        flex-wrap: wrap;
        gap: 5px;
        margin: 8px 0 0;
        padding: 0;
        list-style: none;
    }

    &__tag {
        padding: 2px 8px;
        border-radius: 10px;
        background: #eceeef;
        color: #55595c;
        font-size: 11px;
        line-height: 1.5;

        // The state carries the app's colour for it, painted inline. Draft is
        // white in that scheme, so the outline is what keeps it a chip on a
        // white card rather than floating text.
        &--state {
            border: 1px solid rgba(0, 0, 0, 0.12);
            color: $color-secondary-dark;
        }

        // How long it has stood still is the one number worth interrupting for,
        // and the app's own red is what it is said in.
        &--warn {
            background: #fbe3e5;
            color: #b3303c;
            font-weight: 600;
        }
    }
}

a.wig-card:hover,
a.wig-card:focus-visible {
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.28);
    transform: translateY(-1px);
}

// Deleted: still named, because the assistant named it, but going nowhere.
.wig-card--off {
    opacity: 0.6;

    .wig-card__label {
        text-decoration: line-through;
    }
}

@media (prefers-reduced-motion: reduce) {
    .wig-card {
        transition: none;
    }

    a.wig-card:hover {
        transform: none;
    }
}
</style>
